import { useEffect, useState } from 'react';
import type { BuildInfo } from '@linkode/shared';
import { useTypedTranslation } from '../i18n';
import { Card, Tag } from '../stories/landing';
import '../stories/landing/landing.css';

/**
 * About page displaying complete version information
 * Requirements: 3.3
 */
export function AboutPage() {
  const { t } = useTypedTranslation();
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const baseUrl =
          import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8787' : '');

        const response = await fetch(`${baseUrl}/version`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data: BuildInfo = await response.json();
        setBuildInfo(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch version');
        setBuildInfo({
          env: 'development',
          commitSha: 'unknown',
          buildTime: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVersion();
  }, []);

  const formatBuildTime = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return isoString;
    }
  };

  const getEnvTagVariant = (env: string): 'yellow' | 'teal' | 'primary' => {
    const variants: Record<string, 'yellow' | 'teal' | 'primary'> = {
      development: 'yellow',
      staging: 'primary',
      production: 'teal',
    };
    return variants[env] || 'yellow';
  };

  return (
    <div className="landing-base" style={pageStyle}>
      <h1 className="landing-h2">{t('pages.about.title')}</h1>

      <section style={sectionStyle}>
        <h2 className="landing-h3" style={sectionTitleStyle}>
          {t('pages.about.versionInfo')}
        </h2>

        {loading ? (
          <p style={loadingStyle}>{t('pages.about.loadingVersion')}</p>
        ) : error ? (
          <div style={errorContainerStyle}>
            <p style={errorStyle}>
              {t('pages.about.apiError')}
              {error}
            </p>
            <p className="text-secondary text-sm">{t('pages.about.fallbackNote')}</p>
          </div>
        ) : null}

        {buildInfo && (
          <Card hoverable={false}>
            <table style={tableStyle}>
              <tbody>
                <tr>
                  <td style={labelStyle}>{t('pages.about.environment')}</td>
                  <td style={valueStyle}>
                    <Tag variant={getEnvTagVariant(buildInfo.env)}>
                      {buildInfo.env.toUpperCase()}
                    </Tag>
                  </td>
                </tr>
                <tr>
                  <td style={labelStyle}>{t('pages.about.commitSha')}</td>
                  <td style={valueStyle}>
                    <code style={codeStyle}>{buildInfo.commitSha}</code>
                  </td>
                </tr>
                <tr>
                  <td style={labelStyle}>{t('pages.about.buildTime')}</td>
                  <td style={valueStyle}>{formatBuildTime(buildInfo.buildTime)}</td>
                </tr>
                <tr>
                  <td style={labelStyle}>{t('pages.about.buildTimeIso')}</td>
                  <td style={valueStyle}>
                    <code style={codeStyle}>{buildInfo.buildTime}</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        )}
      </section>

      <section style={sectionStyle}>
        <h2 className="landing-h3" style={sectionTitleStyle}>
          {t('pages.about.project')}
        </h2>
        <Card hoverable={false}>
          <p className="landing-p">{t('pages.about.description')}</p>
          <ul style={listStyle}>
            <li>{t('pages.about.techFrontend')}</li>
            <li>{t('pages.about.techBackend')}</li>
            <li>{t('pages.about.techMonorepo')}</li>
          </ul>
        </Card>
      </section>
    </div>
  );
}

// Styles using design system
const pageStyle: React.CSSProperties = {
  maxWidth: '800px',
  margin: '0 auto',
  padding: '40px 20px 80px',
  backgroundColor: 'var(--color-background)',
  minHeight: '100vh',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '32px',
};

const sectionTitleStyle: React.CSSProperties = {
  borderBottom: '2px solid var(--border-color)',
  paddingBottom: '8px',
  marginBottom: '16px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const labelStyle: React.CSSProperties = {
  padding: '12px 16px 12px 0',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  width: '140px',
  verticalAlign: 'middle',
  fontFamily: 'var(--font-mono)',
  fontSize: '14px',
};

const valueStyle: React.CSSProperties = {
  padding: '12px 0',
  color: 'var(--color-text-primary)',
};

const codeStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-yellow-light)',
  padding: '4px 8px',
  border: '1px solid var(--border-color)',
  fontFamily: 'var(--font-mono)',
  fontSize: '13px',
};

const loadingStyle: React.CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontStyle: 'italic',
};

const errorContainerStyle: React.CSSProperties = {
  marginBottom: '16px',
};

const errorStyle: React.CSSProperties = {
  color: 'var(--color-accent-red)',
  marginBottom: '4px',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '20px',
  lineHeight: '2',
  color: 'var(--color-text-primary)',
};
